import { writable } from 'svelte/store';
import { getFromStorage, saveToStorage } from '../utils/localStorage';

interface NotificationState {
  enabled: boolean;
  speechSynthesisEnabled: boolean;
  badgesEnabled: boolean;
  yellowNotified: boolean;
  redNotified: boolean;
}

const DEFAULT_STATE: NotificationState = {
  enabled: false,
  speechSynthesisEnabled: false,
  badgesEnabled: true,
  yellowNotified: false,
  redNotified: false,
};

/**
 * Initialize state from localStorage
 */
function initState(): NotificationState {
  const enabled = getFromStorage<boolean>('notificationsEnabled', DEFAULT_STATE.enabled);
  const speechSynthesisEnabled = getFromStorage<boolean>('speechSynthesisEnabled', DEFAULT_STATE.speechSynthesisEnabled);
  const badgesEnabled = getFromStorage<boolean>('badgesEnabled', DEFAULT_STATE.badgesEnabled);
  
  return {
    ...DEFAULT_STATE,
    enabled,
    speechSynthesisEnabled,
    badgesEnabled,
  };
}

/**
 * Notification store with localStorage persistence
 */
function createNotificationStore() {
  const { subscribe, set, update } = writable<NotificationState>(initState());

  return {
    subscribe,
    
    /**
     * Enable or disable notifications
     */
    setEnabled: (enabled: boolean) => {
      update((state) => {
        const newState = { ...state, enabled };
        saveToStorage('notificationsEnabled', enabled);
        return newState;
      });
    },
    
    /**
     * Enable or disable speech synthesis
     */
    setSpeechSynthesisEnabled: (speechSynthesisEnabled: boolean) => {
      update((state) => {
        const newState = { ...state, speechSynthesisEnabled };
        saveToStorage('speechSynthesisEnabled', speechSynthesisEnabled);
        return newState;
      });
    },
    
    /**
     * Enable or disable badges
     */
    setBadgesEnabled: (badgesEnabled: boolean) => {
      update((state) => {
        const newState = { ...state, badgesEnabled };
        saveToStorage('badgesEnabled', badgesEnabled);
        return newState;
      });
    },
    
    /**
     * Mark yellow threshold as notified
     */
    markYellowNotified: () => {
      update((state) => ({ ...state, yellowNotified: true }));
    },
    
    /**
     * Mark red threshold as notified
     */
    markRedNotified: () => {
      update((state) => ({ ...state, redNotified: true }));
    },
    
    /**
     * Reset notification tracking (when timer is reset)
     */
    resetTracking: () => {
      update((state) => ({
        ...state,
        yellowNotified: false,
        redNotified: false,
      }));
    },
    
    /**
     * Reset function for testing
     */
    reset: () => {
      set(initState());
      saveToStorage('notificationsEnabled', DEFAULT_STATE.enabled);
      saveToStorage('speechSynthesisEnabled', DEFAULT_STATE.speechSynthesisEnabled);
      saveToStorage('badgesEnabled', DEFAULT_STATE.badgesEnabled);
    },
  };
}

export const notificationStore = createNotificationStore();
